import { FormOfferCollapse } from '@features/offers/components/formOffer/FormOfferCollapse';
import { FormPromotionalOffer } from '@features/offers/components/formOffer/formSections/FormPromotionalOffer';
import { FormSensitiveData } from '@features/offers/components/formOffer/formSections/FormSensitiveData';
import { PageHeader, SwitchController } from '@organisms';
import { Flex, Form, Row } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from 'src/app/framework/organisms/ForgotPasswordButton/ForgotPasswordButton.style';
import { useBreadcrumbs } from 'src/features/layout/hooks/useBreadcrumbs';

import { FormAnalytics } from '../../components/formOffer/formSections/FormAnalytics';
import { FormBasicData } from '../../components/formOffer/formSections/FormBasicaData';
import { FormCounter } from '../../components/formOffer/formSections/FormCounter';
import { FormMobile } from '../../components/formOffer/formSections/FormMobile';
import { FormPhysicProduct } from '../../components/formOffer/formSections/FormPhysicProduct';
import useFormOffer from '../../hooks/useFormOffer';
import { useMobileOptions } from '../../hooks/useMobileOptions';

export const FormOffer: React.FC = () => {
  const navigate = useNavigate();
  const {
    product,
    onSubmit,
    control,
    handleSubmit,
    formType,
    isEditOrView,
    typeCounter,
    isLoading,
    isOfferStandard,
    hasCounter,
    bgCounter,
    multiCounterBgCounter,
    seasonalityOptions,
    typeOptions,
    acquisitionTypeIdsByCodes,
    acquisitionTypeOptions,
    tasFieldDisabled,
    hasPromotionalOffer,
    isPhysicProduct,
    physicProductPrimaryColor,
    hasRetryDiscounts,
    handleApplyCalculatedPrice,
    handleUpdateFullPrice,
    handleUpdateRetryDiscountValue,
    acquisition,
    isFormValid,
    isAvailableMobile,
    isPersistedAsAvailableMobile,
    isPersistedAsPromotionalOffer,
    upsellFlowOptions,
    canCopyOfferLink,
    handleCopyOfferLink,
  } = useFormOffer();

  const { itunesOptions, googleOptions } = useMobileOptions();

  const pageTitle = useMemo(() => {
    const titleByType = {
      create: 'Criar oferta',
      edit: 'Editar oferta',
      view: 'Visualizar oferta',
      duplicate: 'Duplicar oferta',
    };
    if (formType) return titleByType[formType];
    return titleByType.create;
  }, [formType]);

  const setBreadCrumbs = useBreadcrumbs([]);
  useEffect(() => {
    if (pageTitle && product) {
      setBreadCrumbs([
        {
          label: 'Produtos',
          route: '/products',
        },
        {
          label: `Ofertas ${product.name ?? ''}`,
          route: `/products/${product.id}/offers`,
        },
        {
          label: pageTitle,
        },
      ]);
    }
  }, [pageTitle, product]);

  const [activeKeys, setActiveKeys] = useState<string[]>(['sensitiveData']);

  const setFormOfferCollapse = useCallback(
    (key: string, open: boolean) => {
      setActiveKeys(prev =>
        open ? [...prev, key] : prev.filter(k => k !== key)
      );
    },
    [setActiveKeys]
  );

  const RenderLabelWithFormSwitch = useCallback(
    (label: string, fieldName: string, disabled?: boolean) => (
      <Flex align='center' style={{ gap: '24px', height: '28px' }}>
        <div>{label}</div>
        <div onClick={e => e.stopPropagation()}>
          <SwitchController
            label={label}
            control={control}
            defaultValue={false}
            name={fieldName}
            sideLabel
            disabled={disabled}
          />
        </div>
      </Flex>
    ),
    [control]
  );

  useEffect(() => {
    setFormOfferCollapse('counter', hasCounter);
  }, [hasCounter]);
  useEffect(() => {
    setFormOfferCollapse('promotionalOffer', hasPromotionalOffer);
  }, [hasPromotionalOffer]);
  useEffect(() => {
    setFormOfferCollapse('product', isPhysicProduct);
  }, [isPhysicProduct]);
  useEffect(() => {
    setFormOfferCollapse('mobile', isAvailableMobile);
  }, [isAvailableMobile]);

  return (
    <>
      <PageHeader title={pageTitle} subTitle={''} />
      <Form
        disabled={formType === 'view' || isLoading}
        layout='vertical'
        requiredMark='optional'
        onFinish={handleSubmit(onSubmit, errors => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
          if (errors.sensitiveData) setFormOfferCollapse('sensitiveData', true);
          if (errors.analytics) setFormOfferCollapse('analytics', true);
          if (errors.promotionalOffer)
            setFormOfferCollapse('promotionalOffer', true);
          if (errors.counter) setFormOfferCollapse('counter', true);
          if (errors.physicProduct) setFormOfferCollapse('physicProduct', true);
          if (errors.mobile) setFormOfferCollapse('mobile', true);
        })}
        style={{ marginBottom: '-10%' }}
      >
        <FormBasicData control={control} />
        <FormOfferCollapse
          activeKeys={activeKeys}
          onUpdateActiveKeys={setFormOfferCollapse}
          items={[
            {
              key: 'sensitiveData',
              label: 'Dados Sensíveis',
              children: (
                <FormSensitiveData
                  handleRetryDiscounts={handleUpdateRetryDiscountValue}
                  hasRetryDiscounts={hasRetryDiscounts}
                  handleApplyCalculatedPrice={handleApplyCalculatedPrice}
                  handleUpdateFullPrice={handleUpdateFullPrice}
                  control={control}
                  product={product}
                  formType={formType}
                  isEditOrView={isEditOrView}
                  hasPromotionalOffer={hasPromotionalOffer}
                  acquisition={acquisition}
                  isOfferStandard={isOfferStandard}
                  acquisitionTypeOptions={acquisitionTypeOptions}
                  seasonalityOptions={seasonalityOptions}
                  typeOptions={typeOptions}
                  tasFieldDisabled={tasFieldDisabled}
                  acquisitionTypeIdsByCodes={acquisitionTypeIdsByCodes}
                  upsellFlowOptions={upsellFlowOptions}
                />
              ),
            },
            {
              key: 'analytics',
              label: 'Google Analytics e Facebook Pixel',
              children: <FormAnalytics control={control} />,
            },
            {
              key: 'promotionalOffer',
              label: RenderLabelWithFormSwitch(
                'Oferta Promocional (Trial)',
                'hasPromotionalOffer',
                (isEditOrView && isPersistedAsPromotionalOffer) ||
                  formType === 'view'
              ),
              collapsible: hasPromotionalOffer ? undefined : 'disabled',
              children: (
                <FormPromotionalOffer
                  control={control}
                  isDisabled={
                    (isEditOrView && isPersistedAsPromotionalOffer) ||
                    formType === 'view'
                  }
                />
              ),
            },
            {
              key: 'counter',
              label: RenderLabelWithFormSwitch('Contador', 'hasCounter'),
              collapsible: hasCounter ? undefined : 'disabled',
              children: (
                <FormCounter
                  control={control}
                  typeCounter={typeCounter}
                  bgCounter={bgCounter}
                  multiCounterBgCounter={multiCounterBgCounter}
                />
              ),
            },
            {
              key: 'mobile',
              label: RenderLabelWithFormSwitch(
                'Mobile',
                'isAvailableMobile',
                (isEditOrView && isPersistedAsAvailableMobile) ||
                  formType === 'view'
              ),
              collapsible: isAvailableMobile ? undefined : 'disabled',
              children: (
                <FormMobile
                  control={control}
                  itunesOptions={itunesOptions}
                  googleOptions={googleOptions}
                  isDisabled={
                    (isEditOrView && isPersistedAsAvailableMobile) ||
                    formType === 'view'
                  }
                />
              ),
            },
            {
              key: 'physicProduct',
              label: RenderLabelWithFormSwitch(
                'Produto Físico',
                'isPhysicProduct',
                true
              ),
              collapsible: isPhysicProduct ? undefined : 'disabled',
              children: (
                <FormPhysicProduct
                  control={control}
                  physicProductPrimaryColor={physicProductPrimaryColor}
                />
              ),
            },
          ]}
        />

        <div
          style={{
            position: 'fixed',
            width: 'calc(100% - 110px)',
            bottom: 0,
            backgroundColor: '#141414',
            borderTop: '1px solid #333',
          }}
        >
          <Row justify='end' style={{ columnGap: '12px', padding: '24px 0' }}>
            {canCopyOfferLink && (
              <Button
                htmlType='button'
                style={{
                  paddingLeft: '16px',
                  paddingRight: '16px',
                }}
                disabled={false}
                onClick={handleCopyOfferLink}
              >
                Copiar Link
              </Button>
            )}
            <Button
              htmlType='button'
              style={{
                paddingLeft: '16px',
                paddingRight: '16px',
              }}
              disabled={isLoading}
              onClick={() => {
                navigate(-1);
              }}
            >
              Cancelar
            </Button>
            {formType !== 'view' && (
              <Button
                type='primary'
                htmlType='submit'
                style={{
                  paddingLeft: '16px',
                  paddingRight: '16px',
                }}
                disabled={!isFormValid || isLoading}
              >
                Salvar Oferta
              </Button>
            )}
          </Row>
        </div>
      </Form>
    </>
  );
};
